import { useEffect, useRef, useState } from "react";

import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePostApiCall,
  makePostBlobApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { printServerRenderedLabel } from "../../shared/localPrintAgent";
import { isLabelTemplateActive } from "../../shared/labelTemplateCapabilities";
import { useQuickBooksInvoiceStatus } from "../../hooks/useQuickBooksInvoiceStatus";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ProgressModal from "../../components/modals/ProgressModal";
import shipmentsApi from "../../services/shipmentsApi";
import normalizeFlatShipmentItems from "./normalizeFlatShipmentItems";

function filenameFromContentDisposition(header) {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i.exec(header);
  if (!match) return null;
  const raw = match[1].replace(/"/g, "").trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function ShippingDetailsViewModel(shippingId, logout, addSnackbar) {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [shippingLabelEnabled, setShippingLabelEnabled] = useState(false);
  const [shipping, setShipping] = useState(null);
  const onInvoiceCompleted = useRef(() => {});
  const quickBooksInvoice = useQuickBooksInvoiceStatus(shippingId, {
    enabled: Boolean(shipping),
    onCompleted: (request, context) =>
      onInvoiceCompleted.current(request, context),
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [invalidOrderItemIds, setInvalidOrderItemIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPrintingShipping, setIsPrintingShipping] = useState(false);
  const printingShippingRef = useRef(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  useEffect(() => {
    if (!shipping) return undefined;
    let active = true;

    makeGetApiCall("label-templates/capabilities")
      .then((result) => {
        if (active) {
          setShippingLabelEnabled(
            result.success &&
              isLabelTemplateActive(result.data, "shipping_label"),
          );
        }
      })
      .catch(() => {
        if (active) setShippingLabelEnabled(false);
      });

    return () => {
      active = false;
    };
  }, [shipping, shippingId]);

  onInvoiceCompleted.current = (request, context) => {
    if (request?.quickbooks_invoice_id) {
      setShipping((current) =>
        current
          ? {
              ...current,
              quickbooks_invoice_id: request.quickbooks_invoice_id,
              invoice_number: request.invoice_number ?? current.invoice_number,
            }
          : current,
      );
    }
    if (!context?.live) {
      return;
    }
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        "QuickBooks invoice created",
        SnackbarType.Success,
      ),
    );
    getShippingDetails();
  };

  const getShippingDetails = async function () {
    setLoading(true);
    const callResult = await shipmentsApi.getDetails(shippingId);
    if (callResult.success) {
      setShipping({
        ...callResult.data.shipment,
        orderItems: Array.isArray(callResult.data.lines)
          ? callResult.data.lines
          : [],
      });
      setLoading(false);
    } else {
      setLoading(false);
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const updateShippingApiCall = async function (payload) {
    const nextStatus =
      typeof payload?.status === "object"
        ? payload.status?.value
        : payload?.status;
    const currentStatus =
      typeof shipping?.status === "object"
        ? shipping.status?.value
        : shipping?.status;
    const requestPayload = {
      ...payload,
      status: nextStatus,
    };
    delete requestPayload.product_condition;
    const queuesQuickBooksInvoice =
      nextStatus === "shipped" && currentStatus !== "shipped";
    if (queuesQuickBooksInvoice) {
      showProgressModal("Saving shipment and queuing QuickBooks invoice...");
    }

    let callResult;
    try {
      callResult = await makePostApiCall(
        `updateShipping/${shippingId}`,
        requestPayload,
      );
    } finally {
      if (queuesQuickBooksInvoice) {
        hideProgressModal();
      }
    }

    if (callResult.success) {
      setInvalidOrderItemIds([]);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.invoice_request?.message || callResult.data.message,
          SnackbarType.Success,
        ),
      );

      if (queuesQuickBooksInvoice) {
        await quickBooksInvoice.refresh();
      }

      await getShippingDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const invalidIds = Array.isArray(callResult.data?.invalid_order_item_ids)
        ? callResult.data.invalid_order_item_ids
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : [];
      setInvalidOrderItemIds(invalidIds);
      showErrorModal(callResult.data?.message);
      await getShippingDetails();
    }
    return callResult;
  };

  const onFetchPackagedItemByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemByBarcode/${scannedBarcode}`,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addPackagedItemToOrderItem = async function (
    shippingId,
    orderItem,
    packagedItem,
    productTemperature,
    productCondition,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItem: orderItem,
      packagedItem: packagedItem,
      setNewProductTemperature: productTemperature != null,
      ...(productTemperature != null ? { productTemperature } : {}),
      ...(productCondition ? { productCondition } : {}),
    };
    let callResult = await makePostApiCall(
      "addPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const removeShippedCartonApiCall = async function (carton) {
    const callResult = await makePostApiCall(`removeShippedCarton`, {
      id: carton.id,
      barcode: carton.barcode,
      carton_type: carton.carton_type,
    });
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShipping = async function () {
    let callResult = await makePostApiCall(`shippings/delete/${shippingId}`);

    if (callResult.success) {
      window.location = "/shippings";
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.error);
    }
  };

  const printShipping = async function () {
    if (printingShippingRef.current) return;

    printingShippingRef.current = true;
    setIsPrintingShipping(true);
    try {
      const callResult = await makePostBlobApiCall(
        `printShipping/${shippingId}`,
      );

      if (callResult.success) {
        downloadBlob(
          callResult.data,
          filenameFromContentDisposition(
            callResult.headers?.["content-disposition"],
          ) ?? "shipment.pdf",
        );
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(
          callResult.data?.message ?? "We could not print this shipment.",
        );
      }
    } finally {
      printingShippingRef.current = false;
      setIsPrintingShipping(false);
    }
  };

  const quickAddPackagedItemToOrderItem = async function (
    shippingId,
    orderItemId,
    lotItem,
    original_scanned_amount,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItemId: orderItemId,
      lotItem: lotItem,
      original_scanned_amount: original_scanned_amount,
    };
    let callResult = await makePostApiCall(
      "quickAddPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package(s) added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchPackagedItemsForQuickScan = async function (payload) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemsForQuickScan`,
      payload,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getOrderItemShipments = async function (id) {
    const callResult = await shipmentsApi.getPackages(shippingId, id);
    if (callResult.success) {
      return {
        success: true,
        data: normalizeFlatShipmentItems(callResult.data),
      };
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return { success: false };
    }
  };

  const handlePriceSplitResult = async (callResult, message) => {
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success),
      );
      await getShippingDetails();
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal(
        callResult.data?.message ?? "Unable to update shipment pricing.",
      );
    }
    return callResult;
  };

  const onCreatePriceSplit = async (orderItemId, payload) =>
    handlePriceSplitResult(
      await makePostApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits`,
        payload,
      ),
      "Shipment price added",
    );

  const onUpdatePriceSplit = async (orderItemId, splitId, payload) =>
    handlePriceSplitResult(
      await makePutApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits/${splitId}`,
        payload,
      ),
      "Shipment price updated",
    );

  const onDeletePriceSplit = async (orderItemId, splitId) =>
    handlePriceSplitResult(
      await makeDeleteApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits/${splitId}`,
      ),
      "Shipment price deleted",
    );

  const setProductTemperature = async function (
    orderItemId,
    productId,
    temperature,
    productCondition,
  ) {
    let callResult = await makePostApiCall(`setProductTemperature`, {
      shipping_id: shippingId,
      product_id: productId,
      order_item_id: orderItemId,
      temperature: temperature,
      productCondition,
    });
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const createShippingRecordByID = async function () {
    let callResult = await makePostApiCall(
      `/qaReports/createShippingRecordByID`,
      {
        shippingId: shippingId,
      },
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return null;
    }
  };

  const printShippingLabelToPrinter = async function (labelPayload) {
    const printResult = await printServerRenderedLabel(labelPayload);
    if (!printResult?.success) {
      const errorMessage =
        printResult?.data?.subtitle ??
        printResult?.data?.message ??
        printResult?.data?.title ??
        "Print failed. Please try again.";
      showErrorModal("Print error", errorMessage);
    }
  };

  const generateAndPrintShippingLabel = async function (orderItem) {
    showProgressModal("Generating and printing shipping label...");
    try {
      const callResult = await makePostApiCall(`generateShippingLabel`, {
        shipping_id: shippingId,
        order_item_id: orderItem.id,
        product_id: orderItem.product_id,
      });

      if (callResult.success) {
        const body = callResult.data;
        const labelPayload =
          body &&
          typeof body === "object" &&
          (body.barcode !== undefined || body.customer_name !== undefined)
            ? body
            : (body?.data ?? body);
        hideProgressModal();
        await printShippingLabelToPrinter(labelPayload);
      } else {
        hideProgressModal();
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
        return null;
      }
    } catch (error) {
      hideProgressModal();
      showErrorModal(error?.message ?? "Failed to generate shipping label");
      return null;
    }
  };

  return {
    loading,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateShippingApiCall,
    shipping,
    invalidOrderItemIds,
    quickBooksInvoice,
    shippingLabelEnabled,
    onFetchPackagedItemByBarcode,
    addPackagedItemToOrderItem,
    getShippingDetails,
    removeShippedCartonApiCall,
    employeeOptions,
    getUsersForDropdown,
    deleteShipping,
    printShipping,
    isPrintingShipping,
    quickAddPackagedItemToOrderItem,
    fetchPackagedItemsForQuickScan,
    getOrderItemShipments,
    createShippingRecordByID,
    setProductTemperature,
    generateAndPrintShippingLabel,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
  };
}
